import React from "react";
import { Button } from "react-bootstrap";
import { MecordColors } from "Constants/Color";

/**
 * FormActionButtons - Reusable action buttons with fixed styling but configurable actions
 *
 * Usage Examples:
 *
 * // All buttons with custom actions
 * <FormActionButtons
 *   onCancel={handleCancel}
 *   onSaveDraft={handleSaveDraft}
 *   onSave={handleSave}
 * />
 *
 * // Only specific buttons
 * <FormActionButtons
 *   onCancel={handleCancel}
 *   onSave={handleSave}
 *   showSaveDraft={false}
 * />
 *
 * // No hover effects
 * <FormActionButtons
 *   onCancel={handleCancel}
 *   onSave={handleSave}
 *   showHoverEffects={false}
 * />
 *
 * // With loading states
 * <FormActionButtons
 *   onCancel={handleCancel}
 *   onSaveDraft={handleSaveDraft}
 *   onSave={handleSave}
 *   isSaving={isSaving}
 *   isSavingDraft={isSavingDraft}
 * />
 */

interface FormActionButtonsProps {
  // Action handlers
  onCancel?: () => void;
  onSaveDraft?: () => void;
  onSave?: () => void;

  // Configuration
  showCancel?: boolean;
  showSaveDraft?: boolean;
  showSave?: boolean;
  saveButtonLabel?: string;
  showHoverEffects?: boolean;
  wrapperClassName?: string;

  // Loading states
  isSaving?: boolean;
  isSavingDraft?: boolean;
}

const FormActionButtons: React.FC<FormActionButtonsProps> = ({
  onCancel,
  onSaveDraft,
  onSave,
  showCancel = true,
  showSaveDraft = true,
  showSave = true,
  saveButtonLabel = "Save",
  showHoverEffects = true,
  wrapperClassName = "d-flex flex-wrap gap-2",
  isSaving = false,
  isSavingDraft = false,
}) => {
  return (
    <div className={wrapperClassName}>
      {/* Cancel Button */}
      {showCancel && (
        <Button
          variant="link"
          className="px-3 px-md-4"
          style={{
            backgroundColor: MecordColors.LightThemeBGColor,
            color: MecordColors.ActiveText,
            textDecoration: "none",
            border: showHoverEffects ? "1px solid transparent" : "none",
            transition: showHoverEffects ? "border-color 0.15s ease-in-out" : "none"
          }}
          onClick={onCancel}
          onMouseEnter={showHoverEffects ? (e) => {
            e.currentTarget.style.borderColor = MecordColors.ActiveText;
          } : undefined}
          onMouseLeave={showHoverEffects ? (e) => {
            e.currentTarget.style.borderColor = "transparent";
          } : undefined}
        >
          Cancel
        </Button>
      )}

      {/* Save and Draft Button */}
      {showSaveDraft && (
        <Button
          variant="outline-secondary"
          className="px-3 px-md-4"
          style={{
            color: "#000",
            backgroundColor: MecordColors.LightGrayColor,
            border: showHoverEffects ? "1px solid transparent" : `1px solid ${MecordColors.LightGrayColor}`,
            transition: showHoverEffects ? "border-color 0.15s ease-in-out" : "none",
          }}
          onClick={onSaveDraft}
          disabled={isSavingDraft}
          onMouseEnter={showHoverEffects && !isSavingDraft ? (e) => {
            e.currentTarget.style.borderColor = "#6c757d";
          } : undefined}
          onMouseLeave={showHoverEffects && !isSavingDraft ? (e) => {
            e.currentTarget.style.borderColor = "transparent";
          } : undefined}
        >
          {isSavingDraft ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Saving...
            </>
          ) : (
            "Save and Draft"
          )}
        </Button>
      )}

      {/* Save Button */}
      {showSave && (
        <Button
          variant="danger"
          type="submit"
          className="px-3 px-md-4"
          style={{
            backgroundColor: MecordColors.ThemeBGColor,
            borderColor: MecordColors.ThemeBGColor,
            color: "#fff",
            transition: showHoverEffects ? "background-color 0.15s ease-in-out, border-color 0.15s ease-in-out" : "none"
          }}
          onClick={onSave}
          disabled={isSaving}
          onMouseEnter={showHoverEffects && !isSaving ? (e) => {
            e.currentTarget.style.backgroundColor = "#c82333"; // Darker red shade
            e.currentTarget.style.borderColor = "#c82333";
          } : undefined}
          onMouseLeave={showHoverEffects && !isSaving ? (e) => {
            e.currentTarget.style.backgroundColor = MecordColors.ThemeBGColor;
            e.currentTarget.style.borderColor = MecordColors.ThemeBGColor;
          } : undefined}
        >
          {isSaving ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Saving...
            </>
          ) : (
            saveButtonLabel
          )}
        </Button>
      )}
    </div>
  );
};

export default FormActionButtons;
